<ng-container *transloco="let t">
<div class="bulk-upload-container">
  <!-- Drop Zone -->
  <div
    class="drop-zone"
    [class.drag-over]="isDragOver"
    [class.has-files]="showProcessingPanel"
    (dragover)="onDragOver($event)"
    (dragleave)="onDragLeave($event)"
    (drop)="onDrop($event)"
    (click)="fileInput.click()"
    (keydown.enter)="fileInput.click()"
    (keydown.space)="fileInput.click(); $event.preventDefault()"
    tabindex="0"
    role="button"
    [attr.aria-label]="t('portal.bulkUpload.dropZoneAriaLabel')">
    <input
      #fileInput
      type="file"
      multiple
      accept=".pdf,.png,.jpg,.jpeg,.gif,.tif,.tiff,.doc,.docx"
      (change)="onFileSelected($event)"
      style="display: none"
      aria-hidden="true" />

    <div class="drop-zone-content">
      <i class="fa fa-cloud-upload drop-zone-icon"></i>
      <h5 class="drop-zone-title">{{ t('portal.bulkUpload.dropZoneTitle') }}</h5>
      <p class="drop-zone-subtitle">
        {{ t('portal.bulkUpload.dropZoneSubtitle') }}
      </p>
      <span class="drop-zone-hint">
        {{ t('portal.bulkUpload.dropZoneHint') }}
      </span>
    </div>
  </div>

  <!-- Validation Errors -->
  @if (validationErrors.length > 0) {
    <div class="validation-errors" role="alert">
      @for (err of validationErrors; track err) {
        <div class="validation-error-item">
          <i class="fa fa-exclamation-circle me-1"></i>
          {{ err }}
        </div>
      }
      <button class="btn btn-link btn-sm dismiss-errors" (click)="dismissValidationErrors()">
        {{ t('portal.bulkUpload.dismiss') }}
      </button>
    </div>
  }

  <!-- Processing Panel -->
  @if (showProcessingPanel) {
    <div class="processing-panel" [class.flip-out]="isFlippingBack">
      <div class="processing-header">
        <div class="processing-header-copy">
          <h6>
            {{ t('portal.bulkUpload.bulkUploadFiles') }}
            <span class="badge bg-secondary ms-2">{{ fileItems.length }}</span>
          </h6>
          <p class="processing-subtitle">{{ t('portal.bulkUpload.reviewSubtitle') }}</p>
        </div>
      </div>

      <div class="file-list">
        @for (item of fileItems; track item.id) {
          <div class="file-item" [class.file-error]="item.status === 'error'" [class.file-confirmed]="item.status === 'confirmed'" [class.file-sliding-out]="item.isSliding">
            <!-- Thumbnail -->
            <div class="file-thumbnail">
              @if (item.thumbnailUrl) {
                <img [src]="item.thumbnailUrl" alt="thumbnail" />
              } @else {
                <div class="file-thumbnail-placeholder">
                  <i class="fa fa-file-text-o file-icon"></i>
                  <span class="file-thumbnail-extension">
                    {{ item.file.name.split('.').pop()?.toUpperCase() || 'FILE' }}
                  </span>
                </div>
              }
            </div>

            <!-- File info & stages -->
            <div class="file-details">
              <div class="file-name-row">
                <div class="file-name" [title]="item.file.name">{{ item.file.name }}</div>
                <!-- Delete / remove button — available for all non-confirmed statuses -->
                @if (item.status !== 'confirmed') {
                  <button
                    class="btn btn-link btn-sm p-0 ms-2 file-remove-btn"
                    [title]="t('portal.bulkUpload.removeFile')"
                    (click)="removeFile(item); $event.stopPropagation()">
                    <i class="fa fa-trash"></i>
                  </button>
                }
              </div>
              <div class="file-status-row">
                <span class="file-status-label">{{ t('portal.bulkUpload.processingStatus') }}</span>
              </div>

              <!-- Processing stages -->
              <div class="processing-stages">
                @for (stage of item.processingStages; track stage.key) {
                  <span class="stage" [class.stage-complete]="stage.complete" [class.stage-active]="stage.active">
                    <i class="fa" [ngClass]="getStageIcon(stage)"></i>
                    <span class="stage-label">{{ t('portal.bulkUpload.stage.' + stage.key) }}</span>
                  </span>
                  @if (!$last) {
                    <span class="stage-arrow">
                      <i class="fa fa-long-arrow-right"></i>
                    </span>
                  }
                }
              </div>

              <!-- Match result -->
              @if (item.status === 'matched') {
                <div class="match-result match-success">
                  <i class="fa fa-check-circle text-success me-1"></i>
                  {{ t('portal.bulkUpload.matchedTo') }} <strong>{{ item.matchedTaskDescription }}</strong>
                  @if (item.confidence) {
                    <span class="confidence-badge">({{ (item.confidence * 100) | number:'1.0-0' }}% {{ t('portal.bulkUpload.confident') }})</span>
                  }
                  <button
                    class="btn btn-link btn-sm p-0 ms-2 row-confirm-btn"
                    [disabled]="!canConfirmRow(item)"
                    (click)="confirmSingleRow(item); $event.stopPropagation()">
                    <i class="fa fa-check me-1"></i>{{ t('portal.bulkUpload.confirm') }}
                  </button>
                </div>
              }

              @if (item.status === 'needs-selection') {
                <div class="match-result match-manual">
                  <i class="fa fa-exclamation-triangle text-warning me-1"></i>
                  @if (item.extractedDocumentTypeName) {
                    {{ t('portal.bulkUpload.detected') }} <em>{{ item.extractedDocumentTypeName }}</em> &mdash;
                  }
                  {{ t('portal.bulkUpload.selectTask') }}
                  <select
                    class="form-select form-select-sm task-dropdown"
                    [ngModel]="item.selectedTaskId"
                    (ngModelChange)="onTaskSelected(item, $event)">
                    <option [ngValue]="undefined" disabled>{{ t('portal.bulkUpload.chooseTask') }}</option>
                    @for (task of availableTasks; track task.taskId) {
                      <option [ngValue]="task.taskId">
                        {{ task | taskText:'description' }}
                        @if (task.borrowerName) {
                          ({{ task.borrowerName }})
                        }
                      </option>
                    }
                    <option [ngValue]="-1">{{ t('portal.bulkUpload.noMatchingTask') }}</option>
                  </select>
                  <button
                    class="btn btn-link btn-sm p-0 ms-2 row-confirm-btn"
                    [disabled]="!canConfirmRow(item)"
                    (click)="confirmSingleRow(item); $event.stopPropagation()">
                    <i class="fa fa-check me-1"></i>{{ t('portal.bulkUpload.confirm') }}
                  </button>
                </div>

                <!-- Borrower note input — shown when "No matching task" is selected -->
                @if (item.selectedTaskId === -1) {
                  <div class="borrower-note-input mt-2">
                    <textarea
                      class="form-control form-control-sm"
                      rows="2"
                      [placeholder]="t('portal.bulkUpload.borrowerNotePlaceholder')"
                      [ngModel]="item.borrowerNote"
                      (ngModelChange)="onBorrowerNoteChanged(item, $event)">
                    </textarea>
                  </div>
                }
              }

              @if (item.status === 'error') {
                <div class="match-result match-error">
                  <i class="fa fa-times-circle text-danger me-1"></i>
                  {{ item.errorMessage || t('portal.bulkUpload.errorOccurred') }}
                  <button class="btn btn-link btn-sm p-0 ms-2" (click)="retryFile(item)">
                    <i class="fa fa-refresh me-1"></i>{{ t('portal.bulkUpload.retry') }}
                  </button>
                </div>
              }

              @if (item.status === 'split') {
                <div class="match-result match-split">
                  <i class="fa fa-code-fork text-info me-1"></i>
                  {{ t('portal.bulkUpload.splitMessage') }}
                </div>
              }

              @if (item.status === 'confirmed') {
                <div class="match-result match-confirmed">
                  <i class="fa fa-check-circle text-success me-1"></i>
                  {{ t('portal.bulkUpload.attachedSuccessfully') }}
                </div>
              }
            </div>
          </div>
        }
      </div>

      <!-- Actions -->
      <div class="processing-actions">
        @if (hasConfirmedFiles) {
          <button class="btn btn-outline-secondary" (click)="resetUpload()">
            <i class="fa fa-refresh me-1"></i> {{ t('portal.bulkUpload.uploadMore') }}
          </button>
        }

        @if (hasUnconfirmedFiles) {
          <button
            class="btn btn-sm bp-primary-bg-color d-inline-flex align-items-center"
            [disabled]="!canConfirm || isConfirming"
            (click)="confirmAll()">
            @if (isConfirming) {
              <i class="fa fa-spinner fa-spin me-1"></i> {{ t('portal.bulkUpload.confirming') }}
            } @else {
              <i class="fa fa-check text-white me-2"></i>{{ t('portal.bulkUpload.confirmAndSubmitAll') }}
            }
          </button>
        }

        @if (!allProcessed) {
          <span class="processing-status">
            <i class="fa fa-spinner fa-spin me-1"></i>
            {{ t('portal.bulkUpload.processingFiles') }}
          </span>
        }
      </div>
    </div>
  }
</div>
</ng-container>
